<template>
  <div id="zipCode-field">
    <input
      id="zipCode-field-input"
      autoComplete="off"
      :value
      @focus="inputHandler(($event.target as HTMLInputElement).value)"
      @blur="close"
      @input="inputHandler(($event.target as HTMLInputElement).value)"
      @click="open"
      :placeholder="props?.placeholder"
      class="mb-1"
      :style="{ borderRadius: showOptions ? '12px 12px 0 0' : '' }"
    />
    <div
      id="zipCode-options"
      v-show="showOptions"
    >
      <div id="zipCode-field-scrollbar">
        <ul>
          <li
            v-for="(zipCode, index) in zipCodes"
            @click="(e) => selectZipCode(e, zipCode)"
            :key="index"
            class="w-full"
          >
            <span>{{ zipCode.zip_code }} ({{ zipCode.name }})</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch, watchEffect } from 'vue';
  import type { Ref } from 'vue';
  import { useRoadbookStore } from '@/store';
  import apiEsService from '@/services/api/esService';
  import throttle from '@/utils/throttle';
  import toKey from '@/utils/toKey';
  import {
    getFrenchOSM,
    sliceFrenchZipCode,
  } from '@/services/administrativeDivisions/france.service';
  import {
    CHE_OPTIONS,
    getSwitzerlandOSM,
  } from '@/services/administrativeDivisions/switzerland.service';
  import { getSpannishOSM } from '@/services/administrativeDivisions/spain.service';
  import { getBelgiumOSM } from '@/services/administrativeDivisions/belgium.service';
  import { getCanadianOSM } from '@/services/administrativeDivisions/canada.service';

  // General
  const props = defineProps({
    placeholder: {
      type: String,
      default: null,
    },
    debounce: {
      type: Boolean,
      default: true,
    },
  });

  const emit = defineEmits(['updateVisibility']);

  // Stores
  const roadbookStore = useRoadbookStore();

  // Data
  const value = ref<string>();
  const isShowOptions: Ref<boolean> = ref(false);
  const start: Ref<number> = ref(0);
  const size: Ref<number> = ref(10);
  const search: Ref<string> = ref('');
  const zipCodes: Ref<Array<Record<string, any>>> = ref([]);

  const showOptions = computed(() => isShowOptions.value);

  // watch
  watch(
    () => roadbookStore.country,
    async () => {
      value.value = '';
      const res = await searchValue(roadbookStore.metadata.visitor.zip_code);
      emit('updateVisibility', res?.length);
    },
  );

  watchEffect(() => {
    value.value = roadbookStore.cityName
      ? `${roadbookStore.zipCode} (${roadbookStore.cityName})`
      : `${roadbookStore.zipCode}`;
  });

  // methods
  const formatCountryCode = (country_code: string) => {
    return country_code.slice(0, 2).toLowerCase();
  };

  function open() {
    isShowOptions.value = true;
  }

  function close() {
    setTimeout(() => {
      isShowOptions.value = false;
    }, 300);
  }

  const selectZipCode = async (e: Event, zipCode: Record<string, any>) => {
    isShowOptions.value = false;
    value.value = zipCode.zip_code + ` (${zipCode.name})`;
    roadbookStore.setRoadbookModifiedDate();
    await roadbookStore.updateMetadata({
      category: 'visitor',
      field: 'zip_code',
      value: zipCode.zip_code ? zipCode.zip_code : '',
      skipFields: ['city_name'],
    });
    await roadbookStore.updateMetadata({
      category: 'visitor',
      field: 'city_name',
      value: zipCode.name ? zipCode.name : '',
      skipFields: ['zip_code'],
    });
    let osm: { aL6: string; aL4: string } | null = null;
    switch (roadbookStore.country) {
      case 'FRA':
        osm = getFrenchOSM(zipCode.zip_code);
        break;
      case 'ESP':
        osm = getSpannishOSM(zipCode.zip_code);
        break;
      case 'CHE':
        osm = getSwitzerlandOSM(zipCode.zip_code);
        break;
      case 'BEL':
        osm = getBelgiumOSM(zipCode.zip_code);
        break;
      case 'CAN':
        osm = getCanadianOSM(zipCode.zip_code.slice(0, 1));
        break;
      default:
        break;
    }
    const aL6 = osm?.aL6;
    const aL4 = osm?.aL4;
    if (roadbookStore.country === 'FRA') {
      await roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'department',
        value: sliceFrenchZipCode(zipCode.zip_code),
        skipFields: ['zip_code', 'city_name', 'region'],
      });
    } else {
      await roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'department',
        value: aL6 ? toKey(aL6) : '',
        skipFields: ['zip_code', 'city_name', 'region'],
      });
    }

    if (roadbookStore.country === 'CHE') {
      await roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'region',
        value: CHE_OPTIONS.find((it) => it.label === osm?.aL4)?.value ?? '',
        skipFields: ['zip_code', 'city_name'],
      });
    } else {
      await roadbookStore.updateMetadata({
        category: 'visitor',
        field: 'region',
        value: aL4 ? toKey(aL4) : '',
        skipFields: ['zip_code', 'city_name', 'department'],
      });
    }
  };

  const inputHandler = (val: string) => {
    value.value = val;
    throttledInput(val);
  };

  const throttledInput = throttle((value: string) => {
    searchValue(value);
  }, 1000);

  let abortController: AbortController | null = null;
  async function searchValue(val: string) {
    abortController?.abort();
    abortController = new AbortController();

    // Store this abortedController instance to check if the request was aborted below.
    const aborted = abortController;

    search.value = val.charAt(0).toUpperCase() + val.slice(1);
    zipCodes.value = [];

    let res: Array<Record<string, any>>;

    const sizeAll = (s: string) => roadbookStore.country === 'FRA' && /^\d{5}$/.test(s);
    const maxFrenchMunicipalitiesForOneZipCode = 46;
    try {
      if (roadbookStore.country === 'CAN') {
        res = await apiEsService.searchCanadaZipCode(search.value, abortController?.signal);
      } else {
        res = await apiEsService.searchZipCode(
          {
            start: start.value,
            size: sizeAll(search.value.slice(0, 5))
              ? maxFrenchMunicipalitiesForOneZipCode
              : size.value,
            search: search.value,
            country_code: formatCountryCode(roadbookStore.country),
          },
          abortController?.signal,
        );
      }
    } catch (error) {
      console.error('A problem occured retrieveing postal codes.', error);
      return;
    }

    if (aborted.signal.aborted) {
      return;
    }

    zipCodes.value = res;
    return res;
  }
</script>

<style lang="postcss">
  #zipCode-field {
    @apply flex flex-col items-center mt-[16px] relative;
    #zipCode-field-input {
      @apply flex-[1] w-[360px];
    }

    #zipCode-options {
      @apply w-[360px] absolute top-[36px] max-h-[300px] overflow-y-scroll overflow-hidden bg-grey-lightest rounded-b-xl;
      z-index: 15;
      #zipCode-field-scrollbar {
        @apply w-[360px];
        animation: fadeIn 500ms;
        ul {
          @apply flex flex-col items-start;
          li {
            @apply h-[32px] flex items-center px-[8px] cursor-pointer;
            &:hover {
              @apply text-primary;
            }
          }
        }
      }
    }
  }
</style>
